import { QuizStateType } from '../../components/Quiz/state/state';
import { VitalityQuizStateType } from '../../components/VitalityQuiz/state/state';
import { OldQuizAnswersType } from '../../types/quiz';
import { convertAnswerObjectToArray } from '../quizUtils';
import { EligibilityQuizAnswers } from './types';

/**
 * These are all the quiz states that we currently do eligibility checks with.
 */
type AllQuizStates = Partial<
  QuizStateType | VitalityQuizStateType | OldQuizAnswersType
>;

export const DEFAULT_ELIGIBILITY_ANSWERS: EligibilityQuizAnswers = {
  age: null,
  dateOfBirth: null,
  diabetesComplications: [],
  diabetesManagement: [],
  diabetesMedicationPrescriber: null,
  diabetesStatus: null,
  gender: null,
  hasDiabetesDoctorsAppointment: null,
  healthConditions: [],
  healthConditionsComorbidities: [],
  heightCm: null,
  personalHabits: [],
  pregnancy: [],
  secondaryEthnicity: null,
  type2DiabetesMedications: [],
  weightKg: null,
};

const reconcileHasDoctorsAppointment = (
  answers: AllQuizStates,
): boolean | null => {
  if (
    'hasDiabetesDoctorsAppointment' in answers &&
    typeof answers.hasDiabetesDoctorsAppointment === 'boolean'
  ) {
    return answers.hasDiabetesDoctorsAppointment;
  }

  if (
    'hasDiabetesDoctorsAppt' in answers &&
    typeof answers.hasDiabetesDoctorsAppt === 'boolean'
  ) {
    return answers.hasDiabetesDoctorsAppt;
  }

  return null;
};

/**
 * Take the quiz answers for any given quiz state and normalise them
 * to the format that the eligibility checks expect.
 *
 * @param answers - The quiz answers from any of the quiz states.
 * @return An object containing the normalised quiz answers.
 */
export const normaliseQuizEligibilityAnswers = (
  answers: AllQuizStates,
): EligibilityQuizAnswers => {
  const type2DiabetesMedication =
    'type2DiabetesMedication' in answers && answers.type2DiabetesMedication
      ? convertAnswerObjectToArray(answers.type2DiabetesMedication)
      : [];

  const type2DiabetesMedications =
    'type2DiabetesMedications' in answers && answers.type2DiabetesMedications
      ? answers.type2DiabetesMedications
      : [];

  const diabetesComplications = Array.isArray(answers.diabetesComplications)
    ? answers.diabetesComplications
    : convertAnswerObjectToArray(answers.diabetesComplications || {});

  const diabetesManagement = Array.isArray(answers.diabetesManagement)
    ? answers.diabetesManagement
    : convertAnswerObjectToArray(answers.diabetesManagement || {});

  return {
    age: 'age' in answers ? answers.age || null : null,
    dateOfBirth: 'dateOfBirth' in answers ? answers.dateOfBirth || null : null,
    diabetesComplications,
    diabetesManagement,
    diabetesMedicationPrescriber: answers.diabetesMedicationPrescriber
      ? answers.diabetesMedicationPrescriber
      : null,
    diabetesStatus: answers.diabetesStatus ? answers.diabetesStatus : null,
    gender: answers.gender ? answers.gender : null,
    hasDiabetesDoctorsAppointment: reconcileHasDoctorsAppointment(answers),
    healthConditions: answers.healthConditions || [],
    healthConditionsComorbidities: answers.healthConditionsComorbidities || [],
    heightCm: answers.heightCm || null,
    personalHabits: answers.personalHabits || [],
    pregnancy: answers.pregnancy || [],
    secondaryEthnicity:
      ('secondaryEthnicity' in answers && answers.secondaryEthnicity) || null,
    type2DiabetesMedications: [
      ...type2DiabetesMedication,
      ...type2DiabetesMedications,
    ],
    weightKg: answers.weightKg || null,
  };
};
